import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { upperFirst } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, formInput, formRow } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import {
  FinancialAccountCategory,
  FinancialAccountDetails,
  FormModel,
  FinancialAccountCreateModel,
  FinancialAccountUpdateData,
  AddressesFormSection,
} from '@shared/models';
import { Patterns, ErrorUtils, safeJSONparse } from '@shared/utils';
import { accountCategoryFields } from './account-category';
import { cardFields } from './card';
import { bankAccountFields } from './bank-account';
import { addressFields } from './address';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { FinancialAccountService, NotificationService } from '@shared/services';

@Component({
  selector: 'app-edit-financial-account',
  templateUrl: './edit-financial-account.component.html',
  styleUrls: ['./edit-financial-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent, RktIconComponent],
})
export class EditFinancialAccountComponent implements AfterViewChecked, OnDestroy {
  public ref = inject(ChangeDetectorRef);
  public activeModal = inject(ActiveModal);
  private financialAccountService = inject(FinancialAccountService);
  private notificationService = inject(NotificationService);

  accountTypes: FinancialAccountCategory[] = ['EXTERNAL', 'INTERNAL', 'INTEGRATED'];

  formModel: FormModel<FinancialAccountCreateModel> = {};

  accountCategoryFields: FormlyFieldConfig[] = [];

  financialAccountFields = [
    formRow([
      formInput({
        key: 'name',
        label: 'Financial Account Name',
        props: {
          pattern: Patterns.GENERIC_NAME,
          required: true,
        },
        validation: { messages: { pattern: 'Enter a valid Financial Account Name' } },
      }),
      formInput({
        key: 'token',
        label: 'Token',
        props: {
          pattern: Patterns.GENERIC_NAME,
        },
        validation: { messages: { pattern: 'Enter a valid Token' } },
        expressions: {
          hide: 'model.category !== "INTERNAL"',
        },
      }),
    ]),
  ];

  cardFields = cardFields;

  bankFields = bankAccountFields;

  addressFields = addressFields;

  financialAccountForm = new FormGroup({});

  cardForm = new FormGroup({});

  cardInfoForm = new FormGroup({});

  bankForm = new FormGroup({});

  addressForm = new FormGroup({});

  formError = '';

  formLoading = false;

  financialAccountId?: string;

  private destroy$ = new Subject<void>();

  get isCard(): boolean {
    return this.formModel.cardOrBankAccount === 'card';
  }

  get isBank(): boolean {
    return this.formModel.cardOrBankAccount === 'bankAccount';
  }

  get isInternal(): boolean {
    return this.formModel.category === 'INTERNAL';
  }

  get isIntegratedCard(): boolean {
    return this.formModel.category === 'INTEGRATED' && this.formModel.cardOrBankAccount === 'card';
  }

  get isDisabled() {
    return (
      this.formLoading ||
      !this.financialAccountForm.valid ||
      (this.isBank && !this.isInternal && this.bankForm.invalid) ||
      (this.isCard && !this.isIntegratedCard && this.cardForm.invalid) ||
      (this.isCard && !this.isIntegratedCard && this.cardInfoForm.invalid) ||
      (this.isBank && this.addressForm.invalid)
    );
  }

  public ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  public ngOnDestroy(): void {
    this.cardForm.markAsUntouched();
    this.cardInfoForm.markAsUntouched();
    this.bankForm.markAsUntouched();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public modalInitData(editData: FinancialAccountDetails): void {
    this.accountCategoryFields = accountCategoryFields({ isIntegratedAvailable: true });

    if (editData) {
      this.financialAccountId = editData.id;

      const address = editData.bankAccount ? editData.bankAccount.billingAddress : editData.card?.billingAddress;

      this.formModel = {
        ...this.formModel,
        isEdit: true,
        category: editData.category,
        cardOrBankAccount: editData.bankAccount ? 'bankAccount' : editData.card ? 'card' : undefined,
        name: editData.name,
        card: editData.card
          ? {
              firstName: editData.card.firstName,
              middleName: editData.card.middleName,
              lastName: editData.card.lastName,
              nameOnCard: editData.card.fullNameOnCard,
              cardNumber: `**** ${editData.card.cardNumberTail}`,
              expiry: editData.card.expiry,
            }
          : undefined,
        bankAccount: editData.bankAccount
          ? {
              bankName: editData.bankAccount.bankName!,
              routingNo: editData.bankAccount.routingNo!,
              nameOnAccount: editData.bankAccount.nameOnAccount!,
              accountNumber: `**** ${editData.bankAccount.accountNumberTail!}`,
              accountNumberConfirm: `**** ${editData.bankAccount.accountNumberTail!}`,
            }
          : undefined,
        subtype: editData.subtype,

        ...address,
      };
    }
  }

  onSubmit() {
    this.formError = '';

    if (
      (this.isBank && !this.isInternal && this.bankForm.invalid) ||
      (this.isCard && !this.isIntegratedCard && this.cardInfoForm.invalid) ||
      (this.isBank && this.addressForm.invalid)
    ) {
      return;
    }

    this.formLoading = true;

    this.financialAccountService
      .updateFinancialAccount({
        id: this.financialAccountId!,
        updateData: this.financialAccountUpdateData(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (financialAccount) => {
          this.notificationService.displaySuccess(`Financial account updated successfully.`, 'Success');
          this.formLoading = false;
          this.activeModal.close(financialAccount);
        },
        error: (error) => {
          this.handleError(error, 'financialAccountService.updateFinancialAccount');
        },
      });
  }

  private financialAccountUpdateData(): FinancialAccountUpdateData {
    const { name, token, bankAccount, card, category, addressLine1, addressLine2, city, state, postalCode, country } = this.formModel;

    if (category === 'INTERNAL') return { name, token };

    const data = {
      name,
      firstName: card?.firstName,
      middleName: card?.middleName,
      lastName: card?.lastName,
      nameOnAccount: bankAccount?.nameOnAccount,
    };

    if (category === 'EXTERNAL' && addressLine1 && city && state && country && postalCode) {
      return {
        ...data,
        billingAddress: {
          addressLine1: addressLine1,
          addressLine2,
          city: city,
          state: state,
          country: country,
          postalCode: postalCode,
        } as AddressesFormSection,
      };
    }

    return data;
  }

  private handleError(error: string | number, serviceInfo: string): void {
    const isErrorMessage = typeof error === 'string' && !safeJSONparse(error);

    this.formError = this.parseError(isErrorMessage ? error : ErrorUtils.parseExtendedError(error));

    this.formLoading = false;
    ErrorUtils.catchError(`${serviceInfo} error`, error);
  }

  private parseError(error: string) {
    const matches = error.matchAll(/message='(?<msg>[^']+)'/gi);

    const messages = Array.from(matches, (match) => {
      const { msg } = match?.groups ?? {};
      const errorMsg = upperFirst(msg);
      return errorMsg.slice(-1) === '.' ? errorMsg : `${errorMsg}.`;
    });

    if (!messages.length) {
      const errorMessage = upperFirst(error);
      return errorMessage.slice(-1) === '.' ? errorMessage : `${errorMessage}.`;
    }

    return messages.join('\n');
  }
}
